import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_APP_USERS
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const VALID_ROLES = ['admin', 'driver', 'poles_lot10', 'admin_lot10', 'fuel'];

export const APP_USER_ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin — full platform' },
  { value: 'driver', label: 'Driver — fleet (and optional extras below)' },
  { value: 'poles_lot10', label: 'Poles LOT 10 only' },
  { value: 'admin_lot10', label: 'LOT 10 admin — poles, read-only' },
  { value: 'fuel', label: 'Fuel report only' }
];

const parseAccessJson = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

export const buildAccessJson = ({
  polesLot10,
  fuelAccess,
  toolsAccess,
  readOnly,
  lotScope
}) =>
  JSON.stringify({
    polesLot10: Boolean(polesLot10),
    fuelAccess: Boolean(fuelAccess),
    toolsAccess: Boolean(toolsAccess),
    readOnly: Boolean(readOnly),
    lotScope: String(lotScope || '').trim()
  });

export const formatAppUser = (doc) => {
  const access = parseAccessJson(doc.accessJson);
  const hasAccessProfile = access !== null;

  return {
    id: doc.$id,
    email: normalizeEmail(doc.email),
    name: doc.name || '',
    role: VALID_ROLES.includes(doc.role) ? doc.role : 'admin',
    pin: String(doc.pin ?? ''),
    driverKey: doc.driverKey || null,
    active: doc.active !== false,
    hasAccessProfile,
    polesLot10: hasAccessProfile ? Boolean(access.polesLot10) : false,
    fuelAccess: hasAccessProfile ? Boolean(access.fuelAccess) : false,
    toolsAccess: hasAccessProfile ? Boolean(access.toolsAccess) : false,
    readOnly: hasAccessProfile ? Boolean(access.readOnly) : false,
    lotScope: hasAccessProfile ? String(access.lotScope || '') : ''
  };
};

const documentPayloadFromInput = (input) => {
  const email = normalizeEmail(input.email);
  const role = VALID_ROLES.includes(input.role) ? input.role : 'admin';
  const payload = {
    email,
    name: String(input.name || '').trim(),
    role,
    pin: String(input.pin ?? '').trim(),
    driverKey: String(input.driverKey || '').trim(),
    active: input.active !== false
  };

  payload.accessJson = buildAccessJson({
    polesLot10: input.polesLot10,
    fuelAccess: input.fuelAccess,
    toolsAccess: input.toolsAccess,
    readOnly: input.readOnly,
    lotScope: input.lotScope
  });

  return payload;
};

const isMissingAccessJsonError = (err) => {
  const msg = `${err?.message || ''} ${err?.response || ''}`.toLowerCase();
  return (
    msg.includes('accessjson') ||
    msg.includes('unknown attribute') ||
    msg.includes('invalid document structure')
  );
};

const writeDocumentPayload = async (write) => {
  const payload = write.payload;
  try {
    return await write.fn(payload);
  } catch (err) {
    if (!payload.accessJson || !isMissingAccessJsonError(err)) throw err;
    const { accessJson: _omit, ...withoutAccessJson } = payload;
    return await write.fn(withoutAccessJson);
  }
};

/**
 * Look up a login user by email in Appwrite AppUsersSGM.
 * Returns null when not found.
 */
export const findAppUserByEmail = async (email) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) return null;

  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTION_ID_APP_USERS,
    [Query.equal('email', normalizedEmail), Query.limit(1)]
  );

  if (!response.documents?.length) return null;
  return formatAppUser(response.documents[0]);
};

export const listAppUsers = async () => {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTION_ID_APP_USERS,
    [Query.orderAsc('email'), Query.limit(200)]
  );
  return (response.documents || []).map(formatAppUser);
};

export const createAppUser = async (input) => {
  const email = normalizeEmail(input.email);
  if (!email) throw new Error('Email is required.');

  const existing = await findAppUserByEmail(email);
  if (existing) throw new Error('A user with this email already exists.');

  const payload = documentPayloadFromInput(input);
  const doc = await writeDocumentPayload({
    payload,
    fn: (data) =>
      databases.createDocument(DATABASE_ID, COLLECTION_ID_APP_USERS, ID.unique(), data)
  });
  return formatAppUser(doc);
};

export const updateAppUser = async (id, input) => {
  if (!id) throw new Error('User id is required.');
  const payload = documentPayloadFromInput(input);
  const doc = await writeDocumentPayload({
    payload,
    fn: (data) =>
      databases.updateDocument(DATABASE_ID, COLLECTION_ID_APP_USERS, id, data)
  });
  return formatAppUser(doc);
};

export const deleteAppUser = async (id) => {
  if (!id) throw new Error('User id is required.');
  await databases.deleteDocument(DATABASE_ID, COLLECTION_ID_APP_USERS, id);
};
